import React, { useEffect, useState } from "react";
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  Switch,
  Popconfirm,
  Space,
  Typography,
  Card,
  Tag,
  Select,
  Row,
  Col,
} from "antd";
import {
  EditOutlined,
  DeleteOutlined,
  PlusOutlined,
  CheckOutlined,
  CloseOutlined,
  SearchOutlined,
  MinusCircleOutlined,
} from "@ant-design/icons";
import { toastError, toastSuccess } from "../../../utils/toast";
import {
  fetchPackages,
  createPackage,
  updatePackage,
  deletePackageById,
} from "../../../api/subscriptionPackagesApi";

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const TYPE_OPTIONS = [
  { label: "Personal", value: "personal" },
  { label: "Business", value: "business" },
];

const CYCLE_OPTIONS = [
  { label: "Monthly", value: "monthly" },
  { label: "Annual", value: "annual" },
];

const UNIT_OPTIONS = [
  { label: "Month", value: "month" },
  { label: "Year", value: "year" },
];

export default function PackagesPage() {
  const [packages, setPackages] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form] = Form.useForm();
  const [searchForm] = Form.useForm();

  const load = async () => {
    setLoading(true);
    try {
      const data = await fetchPackages();
      const list = Array.isArray(data) ? data : [];
      setPackages(list);
      setFiltered(list);
    } catch (err) {
      toastError("Failed to fetch packages");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const applyFilters = (values) => {
    let next = [...packages];
    const name = (values.name || "").trim().toLowerCase();
    if (name) {
      next = next.filter((row) => (row.name || "").toLowerCase().includes(name));
    }
    if (values.subscription_type) {
      next = next.filter((row) => row.subscription_type === values.subscription_type);
    }
    if (values.billing_cycle) {
      next = next.filter((row) => row.billing_cycle === values.billing_cycle);
    }
    if (values.status) {
      next = next.filter((row) =>
        values.status === "active"
          ? Number(row.is_active) === 1
          : Number(row.is_active) !== 1
      );
    }
    setFiltered(next);
  };

  const resetFilters = () => {
    searchForm.resetFields();
    setFiltered(packages);
  };

  const showCreateModal = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({
      currency: "AED",
      duration_value: 1,
      duration_unit: "month",
      billing_cycle: "monthly",
      subscription_type: "personal",
      cta_type: "checkout",
      display_order: 0,
      is_active: true,
      is_featured: false,
      features: [{ feature_text: "" }],
    });
    setModalVisible(true);
  };

  const showEditModal = (record) => {
    setEditing(record);
    form.setFieldsValue({
      ...record,
      is_active: Number(record.is_active) === 1,
      is_featured: Number(record.is_featured) === 1,
      features:
        Array.isArray(record.features) && record.features.length
          ? record.features.map((f) => ({
              feature_text: f.feature_text || f,
            }))
          : [{ feature_text: "" }],
    });
    setModalVisible(true);
  };

  const handleCreateOrUpdate = async (values) => {
    setSaving(true);
    try {
      const payload = {
        ...values,
        is_active: values.is_active ? 1 : 0,
        is_featured: values.is_featured ? 1 : 0,
        features: (values.features || [])
          .map((f) => (typeof f === "string" ? f : f?.feature_text || ""))
          .filter((text) => String(text).trim() !== ""),
      };

      if (editing) {
        await updatePackage(editing.id, payload);
        toastSuccess("Package updated successfully");
      } else {
        await createPackage(payload);
        toastSuccess("Package created successfully");
      }

      setModalVisible(false);
      form.resetFields();
      load();
    } catch (err) {
      toastError(err?.response?.data?.message || "Failed to save package");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await deletePackageById(id);
      toastSuccess("Package deleted successfully");
      load();
    } catch (err) {
      toastError("Failed to delete package");
    }
  };

  const columns = [
    {
      title: "Plan",
      dataIndex: "name",
      key: "name",
      render: (text, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>{text}</Text>
          {Number(record.is_featured) === 1 && <Tag color="gold">Featured</Tag>}
        </Space>
      ),
      sorter: (a, b) => (a.name || "").localeCompare(b.name || ""),
    },
    {
      title: "Type",
      dataIndex: "subscription_type",
      key: "subscription_type",
      render: (v) => <Tag>{(v || "").replace(/^\w/, (c) => c.toUpperCase())}</Tag>,
    },
    {
      title: "Billing",
      dataIndex: "billing_cycle",
      key: "billing_cycle",
      render: (v) => (v || "").replace(/^\w/, (c) => c.toUpperCase()),
    },
    {
      title: "Price",
      dataIndex: "price",
      key: "price",
      render: (price, record) =>
        record.cta_type === "contact"
          ? "Contact"
          : `${record.currency || "AED"} ${Number(price || 0).toLocaleString()}`,
    },
    {
      title: "Duration",
      key: "duration",
      render: (_, record) => `${record.duration_value} ${record.duration_unit}`,
    },
    {
      title: "Order",
      dataIndex: "display_order",
      key: "display_order",
      sorter: (a, b) => (a.display_order || 0) - (b.display_order || 0),
    },
    {
      title: "Status",
      dataIndex: "is_active",
      key: "is_active",
      render: (isActive) => {
        const active = Number(isActive) === 1;
        return (
          <Tag
            icon={active ? <CheckOutlined /> : <CloseOutlined />}
            color={active ? "success" : "error"}
          >
            {active ? "Active" : "Inactive"}
          </Tag>
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      fixed: "right",
      width: 120,
      render: (_, record) => (
        <Space size="small">
          <Button
            size="small"
            type="text"
            icon={<EditOutlined style={{ color: "#1890ff" }} />}
            onClick={() => showEditModal(record)}
          />
          <Popconfirm
            title="Delete this package? Historical subscriptions keep their snapshot."
            onConfirm={() => handleDelete(record.id)}
            okText="Delete"
            cancelText="Cancel"
            placement="left"
          >
            <Button
              size="small"
              type="text"
              icon={<DeleteOutlined style={{ color: "#ff4d4f" }} />}
              danger
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 16 }}>
      <Card
        title={
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Title level={4} style={{ margin: 0 }}>
              Pricing Packages
            </Title>
            <Button type="primary" icon={<PlusOutlined />} onClick={showCreateModal}>
              New Package
            </Button>
          </div>
        }
        bordered={false}
      >
        <Card style={{ marginBottom: 24 }} bodyStyle={{ padding: 16 }} bordered={false}>
          <Form form={searchForm} layout="inline" onFinish={applyFilters} autoComplete="off">
            <Row gutter={[16, 16]} style={{ width: "100%" }}>
              <Col xs={24} sm={12} md={6}>
                <Form.Item name="name" style={{ width: "100%" }}>
                  <Input placeholder="Plan name" prefix={<SearchOutlined />} allowClear />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={5}>
                <Form.Item name="subscription_type" style={{ width: "100%" }}>
                  <Select placeholder="Type" allowClear>
                    {TYPE_OPTIONS.map((opt) => (
                      <Option key={opt.value} value={opt.value}>
                        {opt.label}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={5}>
                <Form.Item name="billing_cycle" style={{ width: "100%" }}>
                  <Select placeholder="Billing cycle" allowClear>
                    {CYCLE_OPTIONS.map((opt) => (
                      <Option key={opt.value} value={opt.value}>
                        {opt.label}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={4}>
                <Form.Item name="status" style={{ width: "100%" }}>
                  <Select placeholder="Status" allowClear>
                    <Option value="active">Active</Option>
                    <Option value="inactive">Inactive</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={4}>
                <Space>
                  <Button type="primary" htmlType="submit" icon={<SearchOutlined />}>
                    Search
                  </Button>
                  <Button onClick={resetFilters}>Reset</Button>
                </Space>
              </Col>
            </Row>
          </Form>
        </Card>

        <Table
          columns={columns}
          dataSource={filtered}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 10, showSizeChanger: true }}
          scroll={{ x: true }}
          bordered
          size="middle"
        />
      </Card>

      <Modal
        title={editing ? "Edit Package" : "Create Package"}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        onOk={() => form.submit()}
        okText={editing ? "Update" : "Create"}
        confirmLoading={saving}
        width={720}
        destroyOnClose
        centered
      >
        <Form form={form} layout="vertical" onFinish={handleCreateOrUpdate} autoComplete="off">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="name"
                label="Plan name"
                rules={[{ required: true, message: "Please enter the plan name" }]}
              >
                <Input placeholder="e.g. Essential Plan" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="subscription_type"
                label="Subscription type"
                rules={[{ required: true, message: "Select a type" }]}
              >
                <Select>
                  {TYPE_OPTIONS.map((opt) => (
                    <Option key={opt.value} value={opt.value}>
                      {opt.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="billing_cycle"
                label="Billing cycle"
                rules={[{ required: true, message: "Select a billing cycle" }]}
              >
                <Select>
                  {CYCLE_OPTIONS.map((opt) => (
                    <Option key={opt.value} value={opt.value}>
                      {opt.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="cta_type" label="Call to action" initialValue="checkout">
                <Select>
                  <Option value="checkout">Get Started / Checkout</Option>
                  <Option value="contact">Contact Us</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="price"
                label="Price"
                rules={[{ required: true, message: "Enter a price" }]}
              >
                <InputNumber min={0} style={{ width: "100%" }} placeholder="0.00" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="currency" label="Currency">
                <Input placeholder="AED" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="display_order" label="Display order">
                <InputNumber min={0} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="duration_value"
                label="Duration value"
                rules={[{ required: true, message: "Enter duration" }]}
              >
                <InputNumber min={1} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="duration_unit"
                label="Duration unit"
                rules={[{ required: true, message: "Select a unit" }]}
              >
                <Select>
                  {UNIT_OPTIONS.map((opt) => (
                    <Option key={opt.value} value={opt.value}>
                      {opt.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item name="description" label="Description">
                <TextArea rows={3} placeholder="Short plan description" />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.List name="features">
                {(fields, { add, remove }) => (
                  <div>
                    <Text strong>Features</Text>
                    {fields.map((field) => (
                      <Space key={field.key} style={{ display: "flex", marginTop: 8 }} align="baseline">
                        <Form.Item
                          {...field}
                          name={[field.name, "feature_text"]}
                          rules={[{ required: true, message: "Enter a feature" }]}
                          style={{ marginBottom: 0, flex: 1 }}
                        >
                          <Input placeholder="e.g. Response within 24 hours" style={{ width: 520 }} />
                        </Form.Item>
                        <MinusCircleOutlined onClick={() => remove(field.name)} />
                      </Space>
                    ))}
                    <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />} style={{ marginTop: 8 }}>
                      Add feature
                    </Button>
                  </div>
                )}
              </Form.List>
            </Col>
            <Col span={12}>
              <Form.Item name="is_featured" label="Featured" valuePropName="checked">
                <Switch checkedChildren="Yes" unCheckedChildren="No" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="is_active" label="Active" valuePropName="checked">
                <Switch checkedChildren={<CheckOutlined />} unCheckedChildren={<CloseOutlined />} />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </div>
  );
}
